import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../lib/api";
import {
  connectionsApi,
  emailOf,
  failing,
  hideProvider,
  isChanged,
  metaOf,
  shownError,
  type Checked,
  type Connection,
  type ConnectionCheck,
  type ConnectionKind,
  type Mailbox,
} from "../../lib/connectionsApi";
import { useCan } from "../../context/AuthContext";
import { IN_APP } from "../../lib/inApp";

/** The sheet a connection flow has open: connect Meta, wait for Facebook, pick the account or pixel, the key, the mailboxes. */
export type CxSheet = "meta" | "meta-wait" | "meta-account" | "meta-pixel" | "email-key" | "email-mailboxes";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** What a check or a change came to, in one line. */
function outcome(r: Checked): string {
  const n = failing(r.connection.checks).length;
  if (r.connection.state === "ok") return "Checked. Every check passes.";
  if (r.connection.state === "warming") return "Checked. The sending address is still warming up.";
  return `Checked. ${plural(n, "thing", "things")} to fix.`;
}

export interface CxFlowProps {
  productId: string;
  conns: Connection[] | null;
  reload: () => Promise<void>;
  say: (message: string, done?: boolean) => void;
}

/**
 * A product's connection flows, shared by the rows on a computer and the native screens: connect Meta
 * through its sign-in window, the ad account and pixel, the Smartlead key and its mailboxes, Check again
 * and Disconnect. Flashadz runs every check; nothing is ticked by hand.
 */
export function useCxFlows({ productId, conns, reload, say }: CxFlowProps) {
  const [sheet, setSheet] = useState<CxSheet | null>(null);
  const [sheetErr, setSheetErr] = useState<string | null>(null);
  const [sheetBusy, setSheetBusy] = useState(false);
  const [busy, setBusy] = useState<ConnectionKind | null>(null);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const authUrl = useRef<string | null>(null);
  const pickAfterSignIn = useRef(false);
  const [account, setAccount] = useState("");
  const [pixel, setPixel] = useState("");
  const [key, setKey] = useState("");
  const [mailboxes, setMailboxes] = useState<Mailbox[] | null>(null);
  const [chosen, setChosen] = useState<number[]>([]);
  const may = useCan()("admin");

  const byKind = (k: ConnectionKind) => conns?.find((c) => c.kind === k);
  const meta = byKind("meta");

  const open = (s: CxSheet) => {
    setSheetErr(null);
    setKey("");
    setSheet(s);
  };
  const close = useCallback(() => {
    setSheet(null);
    setSheetErr(null);
    setKey("");
  }, []);

  /** A failed write: the plain reason, and when the connection changed meanwhile, the latest from the server. */
  const failed = async (e: unknown, inSheet: boolean) => {
    const m = shownError(e);
    if (e instanceof ApiError && e.status === 409 && isChanged(m)) await reload();
    if (inSheet) setSheetErr(m);
    else say(m, false);
  };

  // --- Meta: sign in through a popup that tells this page how it went ---------------------------------

  // Why the app is never "blocked": inside the phone app the sign-in opens in the phone's browser, which leaves
  // no window here to hand back; the wait below reads the connection until it shows the sign-in's end.
  const openPopup = (url: string) => {
    const opened = window.open(url, "flashadz-meta", "popup,width=600,height=720");
    setPopupBlocked(!IN_APP && !opened);
  };

  const startMeta = async () => {
    setSheetBusy(true);
    setSheetErr(null);
    try {
      const r = await connectionsApi.meta.start(productId);
      if (!r.authUrl) {
        close();
        say("Meta is already connected.");
        await reload();
        return;
      }
      authUrl.current = r.authUrl;
      openPopup(r.authUrl);
      await reload();
      setSheet("meta-wait");
    } catch (e) {
      await failed(e, true);
    } finally {
      setSheetBusy(false);
    }
  };

  /** The sign-in window, again, when the browser stopped it opening. */
  const reopenPopup = () => openPopup(authUrl.current ?? "");

  const signedIn = useCallback(() => {
    pickAfterSignIn.current = true;
    close();
    say("Meta is connected.");
    void reload();
  }, [close, say, reload]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const d = e.data as { type?: string; ok?: boolean; productId?: string | null; message?: string } | null;
      if (!d || d.type !== "flashadz:meta" || (d.productId && d.productId !== productId)) return;
      if (d.ok) signedIn();
      else {
        setSheet("meta");
        setSheetErr(hideProvider(d.message ?? "Meta did not connect. Try again."));
        void reload();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [productId, signedIn, reload]);

  // Why: if the sign-in window cannot reach this page, the saved result still shows on the next read.
  useEffect(() => {
    if (sheet !== "meta-wait") return;
    const look = () => void reload();
    window.addEventListener("focus", look);
    const t = setInterval(look, 4000);
    return () => {
      window.removeEventListener("focus", look);
      clearInterval(t);
    };
  }, [sheet, reload]);
  // Why: the sign-in's end is a new write to the connection after the one the start made.
  const waitFrom = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (sheet !== "meta-wait") {
      waitFrom.current = undefined;
      return;
    }
    if (waitFrom.current === undefined) {
      waitFrom.current = meta?.updatedAt ?? null;
      return;
    }
    if (!meta || meta.updatedAt === waitFrom.current || meta.state === "checking") return;
    const m = metaOf(meta);
    if (m.lastError) {
      setSheet("meta");
      setSheetErr(hideProvider(m.lastError));
    } else if (m.signedIn) signedIn();
  }, [sheet, meta, signedIn]);

  // After signing in, the ad account is picked here when Meta shared more than one usable account.
  useEffect(() => {
    if (!pickAfterSignIn.current || !meta || meta.state === "checking") return;
    pickAfterSignIn.current = false;
    const m = metaOf(meta);
    if (m.adAccountId || m.adAccounts.filter((a) => a.usable).length < 2) return;
    setAccount(m.adAccounts.find((a) => a.usable)?.id ?? "");
    setSheetErr(null);
    setSheet("meta-account");
  }, [meta]);

  const openAccount = () => {
    const m = metaOf(meta);
    setAccount(m.adAccountId ?? m.adAccounts.find((a) => a.usable)?.id ?? "");
    open("meta-account");
  };
  const openPixel = () => {
    setPixel(metaOf(meta).pixelId ?? "");
    open("meta-pixel");
  };

  const sheetCall = async (call: () => Promise<Checked>) => {
    setSheetBusy(true);
    setSheetErr(null);
    try {
      const r = await call();
      close();
      say(outcome(r), r.connection.state !== "fix");
    } catch (e) {
      await failed(e, true);
    } finally {
      setSheetBusy(false);
      await reload();
    }
  };

  const saveAccount = () => sheetCall(() => connectionsApi.meta.adAccount(productId, account));
  const savePixel = () => sheetCall(() => connectionsApi.meta.pixel(productId, pixel.trim()));

  // --- Cold email: the key, then the mailboxes it sends from ---------------------------------------------

  const connectEmail = async () => {
    setSheetBusy(true);
    setSheetErr(null);
    try {
      const r = await connectionsApi.email.connect(productId, key.trim());
      const before = new Set(emailOf(byKind("email")).mailboxIds);
      setMailboxes(r.mailboxes);
      setChosen(r.mailboxes.filter((m) => before.has(m.id)).map((m) => m.id));
      setKey("");
      setSheet("email-mailboxes");
    } catch (e) {
      await failed(e, true);
    } finally {
      setSheetBusy(false);
      await reload();
    }
  };
  /** The mailboxes to choose from, read with the stored key. */
  const openMailboxes = async () => {
    const ids = emailOf(byKind("email")).mailboxIds;
    open("email-mailboxes");
    setMailboxes(null);
    try {
      const r = await connectionsApi.email.list(productId);
      setMailboxes(r.mailboxes);
      setChosen(ids.filter((id) => r.mailboxes.some((m) => m.id === id)));
    } catch (e) {
      setSheetErr(shownError(e));
    }
  };
  const saveMailboxes = () =>
    sheetCall(async () => {
      const r = await connectionsApi.email.mailboxes(productId, chosen);
      setMailboxes(r.mailboxes);
      return r;
    });

  // --- Every connection: Check again and Disconnect -------------------------------------------------------

  const checkAgain = async (k: ConnectionKind) => {
    setBusy(k);
    try {
      say(outcome(await connectionsApi[k].check(productId)));
    } catch (e) {
      await failed(e, false);
    } finally {
      await reload();
      setBusy(null);
    }
  };

  const disconnect = async (k: ConnectionKind) => {
    setBusy(k);
    try {
      await connectionsApi[k].disconnect(productId);
      if (k === "email") setMailboxes(null);
      say("Disconnected.");
    } catch (e) {
      await failed(e, false);
    } finally {
      await reload();
      setBusy(null);
    }
  };

  const start = (k: ConnectionKind) => {
    if (k === "meta") open("meta");
    else open("email-key");
  };

  /** What fixes a failing check, when the owner can fix it here. Others name their place in their reason. */
  const fixFor = (k: ConnectionKind, ck: ConnectionCheck): (() => void) | null => {
    if (!may) return null;
    if (k === "meta") {
      if (ck.id === "connected") return () => open("meta");
      if (ck.id === "ad_account") return metaOf(meta).adAccounts.length > 1 ? openAccount : () => open("meta");
      if (ck.id === "pixel") return openPixel;
      return null;
    }
    if (ck.id === "key") return () => open("email-key");
    if (ck.id === "mailboxes" || ck.id === "connected") return () => void openMailboxes();
    return null;
  };

  return {
    may,
    sheet,
    sheetErr,
    sheetBusy,
    busy,
    popupBlocked,
    hasAuthUrl: !!authUrl.current,
    account,
    setAccount,
    pixel,
    setPixel,
    key,
    setKey,
    mailboxes,
    chosen,
    setChosen,
    byKind,
    open,
    close,
    startMeta,
    reopenPopup,
    openAccount,
    openPixel,
    saveAccount,
    savePixel,
    connectEmail,
    openMailboxes,
    saveMailboxes,
    checkAgain,
    disconnect,
    start,
    fixFor,
  };
}

export type CxFlows = ReturnType<typeof useCxFlows>;
